import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Heart, Sparkles } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { API, useAuth } from "@/App";

const todayStr = () => new Date().toISOString().slice(0, 10);

const IntimacyLoggerCard = () => {
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const [date, setDate] = useState(todayStr());
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API}/wellness/ttc/intimacy/history?days=30`, {
        headers,
        withCredentials: true,
      });
      setEntries(res.data.entries || []);
    } catch {
      /* silent */
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [token]);

  const log = async () => {
    setSaving(true);
    try {
      await axios.post(
        `${API}/wellness/ttc/intimacy`,
        { date, time: time || null, notes: notes || null },
        { headers, withCredentials: true }
      );
      toast.success("Logged 💕");
      setNotes("");
      setTime("");
      setShowForm(false);
      refresh();
    } catch {
      toast.error("Couldn't save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (d) => {
    if (!window.confirm("Remove all entries for this day?")) return;
    try {
      await axios.delete(`${API}/wellness/ttc/intimacy/${d}`, { headers, withCredentials: true });
      toast.success("Removed");
      refresh();
    } catch {
      toast.error("Couldn't remove");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.26 }}
      data-testid="intimacy-logger-card"
    >
      <Card className="glass border-rose-400/20">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-white flex items-center gap-2">
              <Heart className="w-5 h-5 text-rose-300" />
              Intimacy
            </CardTitle>
            <span className="text-xs text-mystic-300" data-testid="intimacy-count">
              {entries.length} this month
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {!showForm ? (
            <Button
              onClick={() => setShowForm(true)}
              data-testid="open-intimacy-form-btn"
              variant="outline"
              className="w-full border-rose-400/30 text-rose-100 hover:bg-rose-500/10 rounded-full"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              Log intimacy
            </Button>
          ) : (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  data-testid="intimacy-date-input"
                  className="bg-mystic-800/50 border border-mystic-600 rounded-lg px-3 py-2 text-white text-sm focus:border-mystic-400 outline-none"
                />
                <input
                  type="time"
                  value={time}
                  onChange={(e) => setTime(e.target.value)}
                  data-testid="intimacy-time-input"
                  className="bg-mystic-800/50 border border-mystic-600 rounded-lg px-3 py-2 text-white text-sm focus:border-mystic-400 outline-none"
                />
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notes (private to you)"
                data-testid="intimacy-notes-input"
                className="w-full bg-mystic-800/50 border border-mystic-600 rounded-lg px-3 py-2 text-white text-sm focus:border-mystic-400 outline-none resize-none"
                rows={2}
              />
              <div className="flex gap-2">
                <Button
                  onClick={log}
                  disabled={saving}
                  data-testid="log-intimacy-btn"
                  className="flex-1 bg-rose-500 hover:bg-rose-400 text-white rounded-full"
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button
                  onClick={() => setShowForm(false)}
                  variant="outline"
                  className="border-white/15 text-mystic-200 rounded-full"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}

          {entries.length > 0 && (
            <div className="pt-2 border-t border-white/5">
              <ul className="space-y-1.5 max-h-32 overflow-y-auto">
                {entries.slice(0, 6).map((e, idx) => (
                  <li
                    key={`${e.date}-${idx}`}
                    className="flex items-center justify-between text-xs"
                    data-testid={`intimacy-history-${e.date}`}
                  >
                    <span className="text-mystic-200">{e.date}{e.time ? ` · ${e.time}` : ""}</span>
                    <button
                      onClick={() => remove(e.date)}
                      className="text-mystic-400 hover:text-rose-300 transition"
                      aria-label={`Delete entries for ${e.date}`}
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default IntimacyLoggerCard;
